import {
  ACTIVE_DELIVERIES_LIMIT,
  ACTIVE_DELIVERY_TYPE,
  cancelDeliveryTransporter,
  createDelivery,
  fetchActiveDeliveriesFromTransporter,
  fetchActiveDeliveriesV2,
  fetchDeliveryDetails,
  fetchEstimatedDeliveryPrice,
  fetchPastDeliveries,
  fetchScheduledDeliveriesFromTransporter,
  recreateDelivery,
  validateDeliveryZone
} from '../../api/deliveries';
import { NetworkError } from '../../utils/apiHelpers';
import { getNextId } from '../../utils/numbers';
import { getItem, setItem } from '../../utils/localStorage';
import { DEFAULT_PICK_UP_LOCATION_ID_KEY } from '../../utils/constants';
import { DeliveryStatus } from '../../api/models/deliveries';
import { fetchTrackingDeliveriesData } from '../../api/deliveriesTracking';
import { getGoogleMapDirections } from '../../api/locations';
import { OrderType } from '../../api/dtos/deliveries';
import { PAST_DELIVERIES_LIMIT } from '../../screens/deliveries/PastDeliveriesPage';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import { fetchDeliveriesTracking } from '../../api/tracking';

const initialState = {
  activeDeliveries: [],
  activeDeliveriesCount: 0,
  activeDeliveriesCursor: null,
  activeDeliveriesPagination: {
    currentPage: 1
  },
  completedDeliveries: [],
  completedDeliveriesPagination: {
    hasNextPage: true
  },
  activeDeliveriesRequestsNumber: 0,
  deliveriesTrackingDataById: {},
  queuedDeliveries: [],
  isSchedulerRunning: false,
  isActiveDeliveriesLoading: true,
  isActiveDeliveriesLoadingError: false,
  isCompletedDeliveriesLoading: true,
  isCompletedDeliveriesLoadingError: false,
  isCompletedDeliveryDetailsLoadingError: false,
  createDeliveryFormState: {
    loading: true
  },
  activeDeliveryTypeParam: 'active'
};

const QUEUE_EXECUTER_INTERVAL = 5 * 1000; // 5 seconds

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const findFirstFreeItem = list => [...list].reverse().find(item => !item.isLoading);

export const deliveries = {
  state: initialState,
  reducers: {
    setActiveDeliveriesLoading(state, isActiveDeliveriesLoading) {
      return {
        ...state,
        isActiveDeliveriesLoading
      };
    },
    setActiveDeliveries(state, activeDeliveries) {
      const { activeDeliveriesRequestsNumber } = state;
      return {
        ...state,
        activeDeliveries,
        isActiveDeliveriesLoading: false,
        isActiveDeliveriesLoadingError: false,
        activeDeliveriesRequestsNumber: activeDeliveriesRequestsNumber + 1
      };
    },
    setActiveDeliveriesCount(state, activeDeliveriesCount) {
      return {
        ...state,
        activeDeliveriesCount
      };
    },
    setActiveDeliveriesRequestsNumber(state, activeDeliveriesRequestsNumber) {
      return {
        ...state,
        activeDeliveriesRequestsNumber
      };
    },
    setCompletedDeliveriesLoading(state, isCompletedDeliveriesLoading) {
      return {
        ...state,
        isCompletedDeliveriesLoading
      };
    },
    setInitialCompletedDeliveries(state, completedDeliveries) {
      return {
        ...state,
        completedDeliveries,
        isCompletedDeliveriesLoading: false,
        isCompletedDeliveriesLoadingError: false
      };
    },
    setMoreCompletedDeliveries(state, completedDeliveries) {
      const { completedDeliveries: prevCompletedDeliveries } = state;
      return {
        ...state,
        completedDeliveries: [...prevCompletedDeliveries, ...completedDeliveries],
        isCompletedDeliveriesLoading: false,
        isCompletedDeliveriesLoadingError: false
      };
    },
    setCompletedDeliveriesHasNextPage(state, hasNextPage) {
      return {
        ...state,
        completedDeliveriesPagination: {
          hasNextPage: hasNextPage
        }
      };
    },
    setCompletedDeliveriesDetails(state, deliveryDetails) {
      const { completedDeliveries: prevCompletedDeliveries } = state;
      const { bookingId } = deliveryDetails;
      const enhancedCompletedDeliveries = prevCompletedDeliveries.map(item => {
        if (bookingId === item.bookingId) {
          return {
            ...item,
            ...deliveryDetails
          };
        } else {
          return item;
        }
      });
      return {
        ...state,
        completedDeliveries: enhancedCompletedDeliveries,
        isCompletedDeliveryDetailsLoadingError: false
      };
    },
    setCompletedDeliveriesLoadingError(state, isCompletedDeliveriesLoadingError) {
      return {
        ...state,
        isCompletedDeliveriesLoadingError
      };
    },
    setCompletedDeliveriesDetailsLoadingError(state, isCompletedDeliveryDetailsLoadingError) {
      return {
        ...state,
        isCompletedDeliveryDetailsLoadingError
      };
    },
    setActiveDeliveriesLoadingError(state, isActiveDeliveriesLoadingError) {
      return {
        ...state,
        isActiveDeliveriesLoadingError
      };
    },
    addDeliveryRequestToQueue(state, deliveryRequest) {
      return {
        ...state,
        queuedDeliveries: [
          {
            item: deliveryRequest,
            id: getNextId(),
            isLoading: false
          },
          ...state.queuedDeliveries
        ]
      };
    },
    setQueueItemLoadingState(state, id, isLoading) {
      const updatedQueuedDeliveries = state.queuedDeliveries.map(item => {
        if (item.id === id) {
          return {
            ...item,
            isLoading
          };
        }
        return item;
      });

      return {
        ...state,
        queuedDeliveries: updatedQueuedDeliveries
      };
    },

    removeQueueItemById(state, id) {
      const queuedDeliveries = state.queuedDeliveries.filter(item => item.id !== id);

      return {
        ...state,
        queuedDeliveries
      };
    },

    setSchedulerState(state, isSchedulerRunning) {
      return {
        ...state,
        isSchedulerRunning
      };
    },

    setTrackingDeliveriesData(state, deliveriesTrackingDataById) {
      return {
        ...state,
        deliveriesTrackingDataById
      };
    },

    setDeliveryFormState(state, data) {
      return {
        ...state,
        createDeliveryFormState: {
          ...state.createDeliveryFormState,
          ...data
        }
      };
    },

    setActiveDeliveriesCurrentPage(state, currentPage) {
      return {
        ...state,
        activeDeliveriesPagination: { currentPage }
      };
    },

    setActiveDeliveryTypeParam(state, activeDeliveryTypeParam) {
      return {
        ...state,
        activeDeliveryTypeParam
      };
    },

    setActiveDeliveriesCursor(state, activeDeliveriesCursor) {
      return {
        ...state,
        activeDeliveriesCursor
      };
    }
  },
  effects: {
    async fetchActiveDeliveries(deliveriesType, state) {
      try {
        const { userGalileoVars } = state.user;
        const isV2Enabled = userGalileoVars[FeatureFlag.IS_ACTIVE_DELIVERIES_V2_ENABLED];
        const {
          activeDeliveriesPagination: { currentPage },
          activeDeliveryTypeParam
        } = state.deliveries;

        this.setActiveDeliveriesLoading(true);

        let activeDeliveries;

        if (isV2Enabled) {
          const { deliveries, nextStartKeyId, nextStartKeyTime } = await fetchActiveDeliveriesV2(
            null,
            activeDeliveryTypeParam
          );
          this.setActiveDeliveries(deliveries);
          this.setActiveDeliveriesCount(deliveries.length);
          this.setActiveDeliveriesCursor(nextStartKeyId ? { nextStartKeyId, nextStartKeyTime } : null);
          activeDeliveries = deliveries;
        } else {
          const paginateFrom = (currentPage - 1) * ACTIVE_DELIVERIES_LIMIT;
          const fetchFn =
            activeDeliveryTypeParam === 'scheduled'
              ? fetchScheduledDeliveriesFromTransporter
              : fetchActiveDeliveriesFromTransporter;
          const { deliveries, count } = await fetchFn(paginateFrom, deliveriesType);
          this.setActiveDeliveries(deliveries);
          this.setActiveDeliveriesCount(count);
          activeDeliveries = deliveries;
        }

        await this.fetchTrackingDeliveriesData();
        return activeDeliveries;
      } catch (error) {
        await this.setActiveDeliveriesLoading(false);
        await this.setActiveDeliveriesLoadingError(true);
      }
    },

    async fetchNextActiveDeliveriesPage(deliveriesType, state) {
      try {
        this.setActiveDeliveriesLoading(true);
        const { activeDeliveriesCursor, activeDeliveryTypeParam } = state.deliveries;
        const { deliveries, nextStartKeyId, nextStartKeyTime } = await fetchActiveDeliveriesV2(
          activeDeliveriesCursor,
          activeDeliveryTypeParam
        );
        this.setActiveDeliveries(deliveries);
        this.setActiveDeliveriesCount(deliveries.length);
        this.setActiveDeliveriesCursor(nextStartKeyId ? { nextStartKeyId, nextStartKeyTime } : null);
        await this.fetchTrackingDeliveriesData();
        return deliveries;
      } catch (error) {
        await this.setActiveDeliveriesLoading(false);
        await this.setActiveDeliveriesLoadingError(true);
        return [];
      }
    },
    async fetchInitialCompletedDeliveries() {
      try {
        const newCompletedDeliveries = await fetchPastDeliveries({ limit: PAST_DELIVERIES_LIMIT });
        this.setInitialCompletedDeliveries(newCompletedDeliveries);
        this.setCompletedDeliveriesLoading(false);
        this.setCompletedDeliveriesLoadingError(false);
        this.checkIfCompletedDeliveriesHasNextPage(newCompletedDeliveries.length);
        return newCompletedDeliveries;
      } catch (error) {
        this.setInitialCompletedDeliveries([]);
        this.setCompletedDeliveriesLoading(false);
        this.setCompletedDeliveriesLoadingError(true);
      }
    },
    checkIfCompletedDeliveriesHasNextPage(portionSize) {
      if (portionSize >= PAST_DELIVERIES_LIMIT) {
        this.setCompletedDeliveriesHasNextPage(true);
      } else {
        this.setCompletedDeliveriesHasNextPage(false);
      }
    },
    async fetchMoreCompletedDeliveries(_, state) {
      try {
        let {
          deliveries: { completedDeliveries }
        } = state;
        this.setCompletedDeliveriesLoading(true);
        const curLength = completedDeliveries.length;

        const lastItem = curLength ? completedDeliveries[curLength - 1] : undefined;
        const startParam = lastItem
          ? {
              id: lastItem.bookingId,
              timestamp: lastItem.createdAt
            }
          : undefined;

        const newCompletedDeliveries = await fetchPastDeliveries({ start: startParam, limit: PAST_DELIVERIES_LIMIT });
        this.setMoreCompletedDeliveries(newCompletedDeliveries);
        this.setCompletedDeliveriesLoading(false);
        this.setCompletedDeliveriesLoadingError(false);
        this.checkIfCompletedDeliveriesHasNextPage(newCompletedDeliveries.length);
      } catch (error) {
        this.setCompletedDeliveriesLoading(false);
        this.setCompletedDeliveriesLoadingError(true);
      }
    },
    async fetchCompletedDeliveryDetails(bookingId) {
      try {
        const details = await fetchDeliveryDetails(bookingId);
        this.setCompletedDeliveriesDetails(details);
      } catch (error) {
        this.setCompletedDeliveriesDetailsLoadingError(true);
      }
    },
    resetCompletedDeliveries() {
      this.setMoreCompletedDeliveries([]);
      this.setCompletedDeliveriesLoading(true);
      this.setCompletedDeliveriesLoadingError(false);
      this.setCompletedDeliveriesHasNextPage(true);
    },
    async setDefaultPickupLocation(locationId) {
      const defaultPickupLocationId = parseInt(getItem(DEFAULT_PICK_UP_LOCATION_ID_KEY));

      if (!defaultPickupLocationId) {
        setItem(DEFAULT_PICK_UP_LOCATION_ID_KEY, locationId);
      }
    },
    async recreateDelivery(bookingId) {
      await recreateDelivery(bookingId);
    },
    async recreateDeliveryAndFetch(bookingId) {
      await this.recreateDelivery(bookingId);
      await this.fetchActiveDeliveries();
    },
    async callCreateDelivery(deliveryRequest, rootState) {
      const { userData, userGalileoVars } = rootState.user;
      const orderTypeGalileo = userGalileoVars?.order_type_per_merchant || OrderType.LMD;
      const isBfcEnabled = userGalileoVars?.[FeatureFlag.IS_BOOKING_FREE_ROUTE_ENABLED];
      return createDelivery({
        userPhoneNumber: userData?.phone,
        ...deliveryRequest,
        orderType: deliveryRequest.orderType || orderTypeGalileo,
        isBookingFree: Boolean(isBfcEnabled)
      });
    },
    async createDelivery(deliveryRequest) {
      /**
       * the logic is relying on the fact that the 'createDelivery' is the only call which can fail in `try...catch`.
       * If it is no longer the case, `catch` logic has to be reconsidered.
       */
      try {
        const deliveryResponse = await this.callCreateDelivery(deliveryRequest);
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_SUBMIT_DEL_REQ_FORM,
          event_version: 2,
          submit_id: deliveryResponse.id,
          booking_uid: deliveryResponse.bookingId
        });
        await this.setDefaultPickupLocation(deliveryRequest.pickupLocation.id);
        /**
         * fetchActiveDeliveries ignores errors atm, once it throws them, this place will
         * be broken - delivery can be duplicated due to this.addDeliveryRequestToQueue(deliveryRequest);
         */
        await this.fetchActiveDeliveries();
      } catch (error) {
        if (error instanceof NetworkError) {
          this.addDeliveryRequestToQueue(deliveryRequest);
          this.startQueueExecutor();
        }
        throw error;
      }
    },

    async startQueueExecutor(_, rootState) {
      const { isSchedulerRunning } = rootState.deliveries;
      if (isSchedulerRunning) {
        return;
      }

      await this.setSchedulerState(true);

      await sleep(QUEUE_EXECUTER_INTERVAL);
      await this.schedulerTick();
    },

    async schedulerTick(_, rootState) {
      const { queuedDeliveries } = rootState.deliveries;

      if (!queuedDeliveries.length) {
        return this.setSchedulerState(false);
      }

      const item = findFirstFreeItem(queuedDeliveries);

      if (item) {
        await this.tryCreateQueuedDelivery(item);
      }

      await sleep(QUEUE_EXECUTER_INTERVAL);
      return this.schedulerTick();
    },

    async tryCreateQueuedDelivery(item) {
      if (item.isLoading) {
        return;
      }
      /**
       * the logic is relying on the fact that the 'createDelivery' is the only call which can fail in `try...catch`.
       * If it is no longer the case, `catch` logic has to be reconsidered.
       * `fetchActiveDeliveries` might lead to false reports.
       */
      try {
        await this.setQueueItemLoadingState(item.id, true);
        await this.callCreateDelivery(item.item);
        await this.removeQueueItemById(item.id);
        await this.fetchActiveDeliveries();
      } catch (error) {
        if (error instanceof NetworkError) {
          await this.setQueueItemLoadingState(item.id, false);
        } else {
          await this.removeQueueItemById(item.id);
          await this.fetchActiveDeliveries();
        }
      }
    },

    async fetchTrackingDeliveriesData(_, rootState) {
      const allowedStatuses = [DeliveryStatus.Assigned, DeliveryStatus.InProgress];
      try {
        const { activeDeliveries } = rootState.deliveries;
        const { userGalileoVars } = rootState.user;
        const isTransporterTrackingEnabled = userGalileoVars[FeatureFlag.IS_TRANSPORTER_TRACKING_ENABLED];

        const fetchTrackingFn = isTransporterTrackingEnabled ? fetchDeliveriesTracking : fetchTrackingDeliveriesData;

        const deliveryIds = activeDeliveries
          .filter(delivery => allowedStatuses.includes(delivery.status))
          .map(delivery => delivery.id);
        if (deliveryIds.length) {
          const trackingData = await fetchTrackingFn(deliveryIds);
          this.setTrackingDeliveriesData(trackingData);
        }
      } catch {
        //TODO handle error
        // ignore the error
      }
    },

    async cancelDelivery({ deliveryId, reason }) {
      await cancelDeliveryTransporter(deliveryId, reason);
      await this.fetchActiveDeliveries(ACTIVE_DELIVERY_TYPE?.MARKETPLACE || 4);
    },

    async cancelOtp(deliveryId) {
      await cancelDeliveryTransporter(deliveryId);
      await this.fetchActiveDeliveries(ACTIVE_DELIVERY_TYPE?.OTP || 1);
    },

    async getDistance(data, rootState) {
      const { userData: { userId } = {} } = rootState.user;
      const { pickupLocation, dropOffLocation } = data;
      this.setDeliveryFormState({ loading: true, distance: undefined });
      try {
        const directions = await getGoogleMapDirections(pickupLocation, dropOffLocation, { userId });
        const distance = directions?.routes?.[0]?.distance;
        await this.setDeliveryFormState({ distance, loading: false });
        this.setDeliveryFormState({ loading: false });
        return { distance, directions };
      } catch (error) {
        this.setDeliveryFormState({ loading: false });
        //TODO handle error
        // what to do?
      }
    },

    async getDeliveryPriceEstimation(data, rootState) {
      const {
        user: { userGalileoVars }
      } = rootState;
      const { pickupLocation, dropOffLocation } = data;

      const orderTypeGalileo = userGalileoVars?.order_type_per_merchant || OrderType.LMD;

      try {
        return fetchEstimatedDeliveryPrice(orderTypeGalileo, pickupLocation, dropOffLocation);
      } catch (error) {
        // return { tripCost: null, currency: null }
      }
    },

    async getDeliveryZoneValidation(location) {
      return validateDeliveryZone(location);
    },

    async getIsFirstDeliveryDone(_, rootState) {
      // TODO: include existing pickup locations & payment methods check here first
      const { activeDeliveries, completedDeliveries } = rootState.deliveries;
      const isFirstDeliveryInitiallyDone = activeDeliveries.length > 0 || completedDeliveries.length > 0;
      if (isFirstDeliveryInitiallyDone) {
        return Promise.resolve(true);
      } else {
        const [activeFetched, completedFetched] = await Promise.all([
          this.fetchActiveDeliveries(ACTIVE_DELIVERY_TYPE.MARKETPLACE),
          this.fetchInitialCompletedDeliveries()
        ]);
        return activeFetched.length > 0 || completedFetched.length > 0;
      }
    },

    async switchActiveDeliveriesMode(activeDeliveryTypeParam) {
      this.setActiveDeliveriesLoading(true);
      this.setActiveDeliveries([]);
      this.setActiveDeliveriesCount(0);
      this.setActiveDeliveryTypeParam(activeDeliveryTypeParam);
      this.setActiveDeliveriesCurrentPage(1);
      this.setActiveDeliveriesRequestsNumber(0);
      this.setActiveDeliveriesLoadingError(false);
      await this.fetchActiveDeliveries(4);
    }
  }
};
